<template>
<div>

  <modal-component size="large" v-model="modal.receiptDetails.active">
    <div slot="title">{{ modal.receiptDetails.title }}</div>
    <receipt-details
      v-if="modal.receiptDetails.paymentID"
      :paymentID="modal.receiptDetails.paymentID"
    ></receipt-details>
  </modal-component>

  <item-history
    :active.sync="itemHistory.active"
    :product="itemHistory.product"
    :type.sync="itemHistory.type"
    v-if="allowActions"
    @updated-product="itemHistory.product = $event, get()"
  ></item-history>

  <modal-component size="medium" v-model="modal.purchases.active">
    <div slot="title">Copy products from purchase invoice</div>
    <model-select
      class="mt-4"
      ref="purchaseInvoiceSelect"
      v-show="true"
      model="invoice"
      form-name="purchase"
      data-vv-as="Select purchase invoice"
      v-model="purchase"
      :list.sync="list.purchase"
      :search-on-mount="false"
      :is-taggable="false"
      :button-new="false"
      :button-edit="false"
      @search="getPurchaseInvoice"
      @select="setPurchaseInvoice"
    ></model-select>

    <button class="btn btn-purple ml-2 my-4" @click="copyInvoice" v-if="purchase && purchase.id && Object.entries(purchaseInvoice).length">
      <i class="far fa-copy"></i>
      <span class="ml-1">Copy invoice items</span>
    </button>
  </modal-component>

  <div class="box" v-if="canChooseContact">
    <div class="flex flex-wrap items-center">
      <div class="w-full text-center" :class="{ 'md:w-1/2': contactType }">
        <button
          class="btn btn-gray-700 font-bold border border-theme-200 py-1 w-32 focus:outline-none mr-2"
          :class="contactType === 'customer' ? 'btn-green text-white' : 'bg-theme text-theme-100 light:bg-theme-darker light:text-theme-900'"
          @click="setContactType('customer')"
        >SALE</button>
        <button
          class="btn btn-gray-700 font-bold border border-theme-200 py-1 w-32 focus:outline-none"
          :class="contactType === 'supplier' ? 'btn-red text-white' : 'bg-theme text-theme-100 light:bg-theme-darker light:text-theme-900'"
          @click="setContactType('supplier')"
        >PURCHASE</button>
      </div>
      <div class="w-full md:w-1/2 mt-4" v-show="contactType">
        <model-select
          ref="customerModelSelect"
          v-show="contactType === 'customer'"
          model="customer"
          form-name="customer"
          data-vv-as="Select customer"
          v-model="invoice.contact"
          :list.sync="list.customer"
          :search-on-mount="false"
          :is-taggable="true"
          @select="setContact"
        ></model-select>
        <model-select
          ref="supplierModelSelect"
          v-show="contactType === 'supplier'"
          model="supplier"
          form-name="supplier"
          data-vv-as="Select supplier"
          v-model="invoice.contact"
          :list.sync="list.supplier"
          :search-on-mount="false"
          :is-taggable="true"
          :button-new="false"
          :button-edit="false"
          @select="setContact"
        ></model-select>

        <model-select
          class="mt-4"
          ref="supplierAccountModelSelect"
          v-if="contactType === 'supplier' && invoice.contact.id"
          model="supplier-account"
          form-name="supplier_account"
          data-vv-as="Select supplier account"
          v-model="supplierAccount"
          :list.sync="list.supplierAccount"
          :search-on-mount="false"
          :is-taggable="true"
          :button-new="false"
          @select="setSupplierAccount"
          @search="filterSupplierAccounts"
        ></model-select>
      </div>
    </div>
  </div>

  <div class="text-right" v-if="hasItems && hasPermission('credit-note.create')">
    <button class="btn btn-purple" @click="createCreditNote">
      <i class="fas fa-exchange-alt"></i>
      <span class="ml-2">Create Credit Note</span>
    </button>
  </div>


  <div class="box" v-if="invoice.id && (invoice.type !== 'purchases' || supplierAccount.id)">
    <div class="flex flex-wrap">
      <div class="w-full md:w-1/2">
        <img class="h-10" :src="!config.logoPrintPath || url.resolve(config.URL.API.host, config.logoPrintPath)">
      </div>
      <div class="w-full md:w-1/2 md:text-right">
        <p
          class="font-bold uppercase"
          :class="invoice.type === 'purchases' ? 'text-red-500' : 'text-green-500'"
          v-if="preview"
        >{{ invoice.type.slice(0, -1) }}</p>

        <div class="bg-green-500 text-white inline-block px-1 leading-tight rounded" v-if="!!invoice.cash_on_delivery">Cash</div>

        <div class="flex items-center justify-end">
          <p class="text-2xl font-bold">
            Invoice
          </p>
          <router-link
            class="btn btn-orange text-xs ml-2"
            :to="'/invoice/edit/' + invoice.id"
            v-if="preview && allowEdit && hasPermission('invoice.edit')"
          >
            <i class="fas fa-edit"></i>
            <span class="ml-1">Edit</span>
          </router-link>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap">
      <div class="w-full md:w-8/12" v-if="invoice.contact">
        <div class="mt-3 pb-3 border-b border-theme-200 light:border-theme-600">
          <a class="text-link" @click.stop="$router.push($contactPageByType(invoice.contact, 'details'))">
            <span class="mb-0 text-lg">
              {{ invoice.contact.first_name }} {{ invoice.contact.last_name }}
              <template v-if="invoice.contact.company_name"> ({{ invoice.contact.company_name }})</template>
              <small>#{{ invoice.contact.id }}</small>
            </span>
          </a>
          <p class="text-sm" v-if="supplierAccount.id">
            Supplier account: {{ supplierAccount.display_name }}
          </p>

          <p v-if="invoice.job && invoice.job.vehicle" class=" leading-none">
            {{ invoice.job.vehicle.make }} {{ invoice.job.vehicle.model }}<br>
            <span class="text-xs">Plate numbers: {{ invoice.job.vehicle.plate_numbers }}</span><br>
            <span class="text-xs">Odometer reading at date: {{ invoice.job.odometer_reading_at_date.toLocaleString() }}</span>
          </p>
        </div>

        <div class="flex flex-wrap text-sm mt-2">
          <div class="w-full md:w-1/2">
            <div v-for="(addr, i) in invoice.contact.addresses" :key="'address-' + i">
              <p class="text-lg font-bold">Address</p>
              <address>
                {{ addr.address_1 }},<br>
                {{ addr.city }},
                <span v-if="addr.state">{{ addr.state }}, </span>
                <span>{{ addr.post_code }}</span>,<br>
                <span>{{ CountryByID(addr.country_id).name }}</span>
              </address>
            </div>
          </div>
          <div class="w-full md:w-1/2">
            <p class="text-lg font-bold">Phone</p>
            <p class="font-bold" v-for="(phone, i) in invoice.contact.phone_numbers" :key="'phone-' + i">
              <template v-if="phone.countryData = CountryByID(phone.country_id)">
                <span :title="phone.countryData.name">{{ '+' + phone.countryData.calling_code }}</span> {{ phone.number }}
              </template>
            </p>
          </div>
        </div>
      </div>

      <div class="w-full md:w-4/12 flex flex-col">
        <div class="self-end pb-3 border-b border-theme-200 light:border-theme-600 text-sm">
          <table>
            <tbody>
              <tr>
                <td colspan="2" class="font-bold text-lg text-right">#{{ invoice.id }}</td>
              </tr>
              <tr>
                <td>Date:</td>
                <td class="text-right">
                  {{ $moment(invoice.created_at).format(config.dateFormats.display) }}
                </td>
              </tr>
              <tr>
                <td>Status:</td>
                <td class="text-right">
                  <div
                    class="text-white rounded inline-block leading-tight px-1"
                    :class="isSettled ? 'bg-green-500' : 'bg-red-500'"
                  >
                    {{ isSettled ? 'Settled' : 'Not settled'}}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="text-sm text-right mt-2">
          <p class="text-base font-bold">{{ config.companyName }}</p>
          <p>{{ config.companyAddress }}, {{ config.companyCity }}</p>
          <p v-if="config.companyState">{{ config.companyState }}, </p>
          <p>{{ config.companyPostCode }}, {{ config.companyCountry }}</p>
          <p>{{ config.companyPhone }}</p>
          <p v-if="config.companyVATNumber"><strong>VAT:</strong> {{ config.companyVATNumber }}</p>
          <p v-if="config.companyIBAN"><strong>IBAN:</strong> {{ config.companyIBAN }}</p>
          <p v-if="config.companyAccountNumber"><strong>Account No:</strong> {{ config.companyAccountNumber }}</p>
        </div>
      </div>
    </div>

    <div class="w-1/3 mt-4" v-if="invoice.type === 'purchases'">
      <label for="external-id">External ID</label>
      <div class="flex">
        <div class="flex-grow">
          <input name="external_id" type="text" class="form-control" v-model="invoice.external_id">
        </div>
        <button
          type="button"
          class="btn btn-blue ml-2"
          @click="save"
          v-if="invoice.external_id !== initial.external_id"
        >
          <i class="fas fa-check"></i>
          <span class="ml-2">Save</span>
        </button>
      </div>
    </div>

    <table-editor
      v-if="invoice && invoice.items && invoice.contact.id"
      :allow-actions="allowActions"
      :items="invoice.items"
      :transaction-type="invoice.type"
      :contactID="invoice.contact.id"
      :contact-type="contactType"
      :preSelectedProductID="preSelectedProductID"
      :supplier-account="supplierAccount"
      v-model="editor.item.active"
      @item-deleted="save"
      @item-added="save"
      @openItemHistory="openItemHistory"
    />

    <div class="my-4" v-if="!editor.item.active && allowActions">
      <button type="button" class="btn py-2 px-3 btn-purple" @click="editor.item.active = true">
        <i class="fas fa-plus"></i>
        <span class="ml-3">Add item</span>
      </button>
      <button class="btn py-2 px-3 btn-blue ml-3" @click="openPurchaseModal()" v-if="!isSettled && invoice.type === 'sales'">
        <i class="far fa-copy"></i>
        <span class="ml-3">Copy from Purchase</span>
      </button>
    </div>



    <div class="flex flex-wrap mt-8">
      <div class="w-full md:w-1/2">
        <template v-if="!isPaidFully && !inModal && !isSettled && hasAllPermissions(['invoice.edit', 'payment.create'])">
          <p class="text-xl font-bold">Payments</p>

          <div class="border border-theme-200 light:border-theme-600 py-6 px-4">
            <template v-if="!editor.payment.active">
              <button type="button" class="btn btn-green font-bold py-2 px-4" @click="editor.payment.active = true">
                <i class="fas fa-receipt"></i>
                <span class="ml-3">Create payment</span>
              </button>
            </template>

            <payment-editor
              v-model="editor.payment.data"
              :payment-type="editor.payment.type"
              :active.sync="editor.payment.active"
              :close-button="true"
              :amount="totalRemaining"
              @save="savePayment"
            ></payment-editor>
          </div>
        </template>
      </div>
      <div class="w-full md:w-1/2 flex flex-col mt-4 md:mt-4">

        <div class="self-end text-lg">
          <table class="w-full">
            <tbody>
              <tr>
                <td>Sub total:</td>
                <td class="font-bold text-right">{{ $formatMoney(total.sub) }}</td>
              </tr>
              <tr v-if="total.discount">
                <td>Discount:</td>
                <td class="font-bold text-right">{{ $formatMoney(total.discount) }}</td>
              </tr>
              <tr v-if="total.tax">
                <td>Tax:</td>
                <td class="font-bold text-right">{{ $formatMoney(total.tax) }}</td>
              </tr>
              <tr class="border-t border-theme-200 light:border-theme-600 text-xl">
                <td class="pt-4 pr-4">Grant total:</td>
                <td class="pt-4 font-bold text-right">{{ $formatMoney(total.sub - total.discount + total.tax) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="editor.payment.active || invoice.payments.length" class="mt-8 px-2 py-1 border border-theme-200 light:border-theme-600">
            <p class="text-xl font-bold text-center border-blyat mb-2">Payments</p>

            <div
              class="bg-theme-600 hover:bg-theme-800 cursor-pointer mb-1"
              v-for="(payment, i) in invoice.payments" :key="'total-payments-' + i"
              @click="openReceipt(payment.id)"
            >
              <div class="flex flex-wrap items-center px-2 py-2 text-sm">
                <div class="w-1/2 px-2">
                  <p>{{ payment.method }} ({{ payment.status }})</p>
                  <p class="text-xs whitespace-no-wrap break-all">{{ $moment(payment.created_at).format(config.dateTimeFormats.display) }}</p>
                </div>
                <div class="w-1/2 px-2 text-right">
                  <p class="text-red font-bold" v-if="isReceipt(payment)">GROSS</p>
                  <p class="font-bold">{{ $formatMoney(payment.value) }}</p>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap items-center px-2 py-2 my-1 text-sm border border-theme-200 light:border-theme-600" v-if="editor.payment.active">
              <div class="w-2/3 px-2">
                <p>{{ editor.payment.data.method }} (new)</p>
                <p class="text-xs whitespace-no-wrap break-all" v-if="timeNow">{{ timeNow.format(config.dateTimeFormats.display) }}</p>
              </div>
              <div class="w-1/3 px-2">
                <p class="font-bold">{{ $formatMoney(editor.payment.data.value) }}</p>
              </div>
            </div>

            <div v-if="!invoice.receipts || !invoice.receipts.length">
              <div class="flex flex-wrap items-center pt-2 mt-1 text-sm" v-if="invoice.payments.length > 0">
                <div class="w-1/2 px-2">
                  <p>Payments total:</p>
                </div>
                <div class="w-1/2 px-2">
                  <p class="font-bold text-right">{{ $formatMoney(total.payments + (editor.payment.active ? +editor.payment.data.value : 0)) }}</p>
                </div>
              </div>
              <div class="flex flex-wrap items-center pb-2 mb-1 border-t border-theme-200 light:border-theme-600" v-if="!isPaidFully">
                <div class="w-1/2 px-2">
                  <p>Remaining:</p>
                </div>
                <div class="w-1/2 px-2">
                  <p class="font-bold text-right">{{ $formatMoneyToFixed(totalRemaining - (editor.payment.active ? +editor.payment.data.value : 0)) }}</p>
                </div>
              </div>
            </div>

          </div>

          <div v-if="invoice.credit_notes.length" class="self-end mt-8 px-2 py-1 border border-theme-200 light:border-theme-600">
            <p class="text-xl font-bold text-center border-blyat mb-2">Credit notes</p>

            <router-link
              tag="div"
              :to="'/credit-note/edit/' + creditNote.id"
              class="bg-theme-600 hover:bg-theme-800 cursor-pointer mb-1"
              v-for="(creditNote, i) in invoice.credit_notes"
              :key="'credit-notes-' + i"
              @click="openReceipt(payment.id)"
            >
              <div class="flex flex-wrap items-center px-2 py-2 text-sm">
                <div class="w-3/6 px-2">
                  <p>#{{ creditNote.id }}</p>
                  <p class="text-xs" :class="creditNote.settlement_date ? 'text-green-500' : 'text-red-500'">
                    {{ creditNote.settlement_date ? 'Settled' : 'Not settled' }}
                  </p>
                </div>
                <div class="w-3/6 px-2 text-right">
                  <div v-if="creditNote.settlement_date || creditNote.total.remaining <= 0 || creditNote.total.paid <= 0">
                    <p class="font-bold" :class="creditNote.settlement_date ? 'text-green-500' : 'text-red-500'">
                      {{ $formatMoney(creditNote.total.sub - creditNote.total.discount + creditNote.total.tax) }}
                    </p>
                  </div>
                  <div v-else>
                    <p>Total: {{ $formatMoney(creditNote.total.sub - creditNote.total.discount + creditNote.total.tax) }}</p>
                    <p>
                      Paid: {{ $formatMoney(creditNote.total.sub - creditNote.total.discount + creditNote.total.tax - creditNote.total.remaining) }}
                    </p>
                    <p class="text-red-500">{{ $formatMoney(creditNote.total.remaining) }}</p>
                  </div>
                </div>
              </div>
            </router-link>
          </div>

          <div class="text-center mt-4" v-if="allowActions">
            <div
              class="btn select-none text-sm px-4 rounded-r-none border"
              :class="!invoice.cash_on_delivery ? 'bg-green-500 text-white border-green-200' : 'bg-theme border-theme-200 light:border-theme-600 text-theme-200 light:text-theme-600'"
              @click="setCOD(false)"
            >
              <span class="font-bold tracking-wide">CREDIT</span>
            </div>
            <div
              class="btn select-none text-sm px-4 rounded-l-none border"
              :class="invoice.cash_on_delivery ? 'bg-green-500 text-white border-green-200' : 'bg-theme border-theme-200 light:border-theme-600 text-theme-200 light:text-theme-600'"
              @click="setCOD(true)"
            >
              <span class="font-bold tracking-wide">CASH ON DELIVERY</span>
            </div>
          </div>

        </div>

      </div>
    </div>

    <div class="flex flex-wrap flex-col mt-8">
      <div class="self-end text-right">
        <div
          class="btn px-3 py-2 btn-purple"
          @click="settle()"
          v-if="invoice.items.length && isPaidFully && !isSettled && !inModal && hasPermission('invoice.edit')"
        >
          <i class="fas fa-check"></i>
          <span class="ml-2">Settle invoice</span>
        </div>

        <router-link
          :to="'/invoice/edit/' + invoice.id"
          class="btn px-3 py-2 btn-orange ml-3"
          v-if="preview && allowEdit && hasPermission('invoice.edit')">
          <i class="fas fa-edit"></i>
          Edit Invoice
        </router-link>

        <div class="btn px-3 py-2 btn-blue ml-3" @click="print">
          <i class="fas fa-print"></i>
          <span class="ml-2">Print</span>
        </div>
      </div>
    </div>

  </div>

</div>
</template>

<script>
import axios from 'axios'
import path from 'path'
import url from 'url'
import { mapActions, mapGetters } from 'vuex'
const ModalComponent = resolve => require(['@/components/Modal'], resolve)
const ItemHistory = resolve => require(['@/components/pages/invoices/ItemHistory'], resolve)
const ReceiptDetails = resolve => require(['@/components/pages/receipts/Details'], resolve)
const PaymentEditor = resolve => require(['@/components/pages/payments/Editor'], resolve)
const TableEditor = resolve => require(['@/components/TableEditor'], resolve)
import Fuse from 'fuse.js'

var pdfMake = require('pdfmake/build/pdfmake.js')
var pdfFonts = require('pdfmake/build/vfs_fonts.js')
pdfMake.vfs = pdfFonts.pdfMake.vfs

export default {
  name: 'invoice-edit',
  props: {
    invoiceID: {},
    invoiceType: {
      type: String,
      default: 'purchases',
    },
    contactID: {},
    job: {},
    contactEditable: {
      type: Boolean,
      default: true,
    },
    inModal: Boolean,
    preSelectedProductID: {},
  },
  components: {
    ItemHistory,
    ReceiptDetails,
    PaymentEditor,
    ModalComponent,
    TableEditor,
  },
  data() {
    return {
      url,
      mode: null,
      preview: false,
      invoice: {},
      purchase: {},
      purchaseInvoice: {},
      editID: null,
      list: {
        customer: [],
        supplier: [],
        supplierAccount: [],
        purchase: [],
      },
      supplierAccount: {},
      contactType: '',
      timeNow: this.$moment(),
      modelSelectRefs: {customer: {}, supplier: {}},
      initial: {
        contact: {},
        external_id: '',
      },
      itemHistory: {
        active: false,
        product: {},
        type: '',
      },
      editor: {
        item: {
          active: false,
        },
        payment: {
          active: false,
          type: '',
          data: {},
        }
      },
      modal: {
        receiptDetails: {
          active: false,
          title: '',
          paymentID: null,
        },
        purchases: {
          active: false,
          title: '',
          purchaseID: null
        }
      },
      delays: {
        timeNow: {
          d: null,
          t: 1000,
        },
      }
    }
  },
  watch: {
    'invoice.items'() {
      this.invoice.items.map(v => {
        v.total = {
          raw: v.value * v.quantity,
        }
        v.total.discount = v.total.raw * v.discount / 100
        v.total.tax = (v.total.raw - v.total.discount) * v.tax / 100
        v.total.all = v.total.raw - v.total.discount + v.total.tax
      })
    },
    job() {
      this.$set(this.invoice, 'job', this.job)
    },
    invoiceID() {
      this.setMode()
    },
  },
  computed: {
    canChooseContact() {
      if (this.invoice.type === 'sales' && (this.invoice.contact && this.invoice.contact.id)) {
        return false
      }
      if (this.invoice.type === 'purchases' && this.supplierAccount.id) {
        return false
      }

      return this.contactEditable && this.allowActions && !this.hasItems
    },
    hasItems() {
      return !this.invoice.items || this.invoice.items.length > 0
    },
    allowActions() {
      return !this.inModal && !this.preview && this.allowEdit
    },
    allowEdit() {
      // allow editing of an unsettled invoice, only when created in the current month
      return !this.isSettled && this.$moment(this.invoice.created_at).isSameOrAfter(this.$moment().startOf('month'))
    },
    totalRemaining() {
      return this.total.sub - this.total.discount + this.total.tax - +this.total.payments
    },
    isPaidFully() {
      return Math.round(this.totalRemaining * 100) / 100 <= 0
    },
    isSettled() {
      return !!this.invoice.settlement_date
    },
    total() {
      if(!this.invoice.items) {
        return {}
      }

      const total = this.invoice.items.map(e => e.total)
      return {
        sub: total.map(e => e.raw).reduce((a, b) => a + b, 0),
        discount: total.map(e => e.discount).reduce((a, b) => a + b, 0),
        tax: total.map(e => e.tax).reduce((a, b) => a + b, 0),
        payments: this.invoice.payments.map(e => e.value).reduce((a, b) => a + b, 0)
      }
    },
    ...mapGetters({
      CountryByID: 'countries/countryByID',
      config: 'config/config',
      hasPermission: 'user/hasPermission',
      hasAllPermissions: 'user/hasAllPermissions',
    })
  },
  mounted() {
    this.setMode()
    this.delays.timeNow.d = setInterval(() => {
      this.timeNow = this.$moment()
    }, this.delays.timeNow.t)
    // get financial accounts
    this['config/getFinancialAccounts']()
  },
  destroyed() {
    clearInterval(this.delays.timeNow.d)
  },
  methods: {
    setCOD(value) {
      this.invoice.cash_on_delivery = value
      this.save()
    },
    filterSupplierAccounts(query) {
      let fuse = new Fuse(this.list.supplierAccount, {
        keys: ['display_name']
      })
      fuse.search(query)
    },
    createCreditNote() {
      this.$swal.fire({
        title: 'Create Credit Note',
        text: 'Are you sure?',
        type: 'question',
        showCancelButton: true,
      })
        .then((result) => {
          if (result.value) {
            // create and redirect
            axios.post('credit-notes', {
              invoice_id: this.invoice.id,
              contact_id: this.invoice.contact.id,
              type: this.invoice.type,
              // items: this.invoice.items,
              items: [],
              payments: [],
            }).then((response) => {
              this.$router.push('/credit-note/edit/' + response.data.id)
            })
          }
        })
    },
    isReceipt(payment) {
      if (!this.invoice.receipts || !this.invoice.receipts.length) {
        return false
      }
      for (let i in this.invoice.receipts) {
        if (this.invoice.receipts[i].payment_id === payment.id) {
          return true
        }
      }
      return false
    },
    openReceipt(paymentID) {
      this.modal.receiptDetails.paymentID = paymentID
      this.modal.receiptDetails.active = true
    },
    openItemHistory(product) {
      if (this.itemHistory.product.id === product.id) {
        this.closeItemHistory()
      }
      this.itemHistory.active = true
      this.itemHistory.product = product
      this.itemHistory.type = this.contactType === 'supplier' ? 'purchases' : 'sales'
    },
    closeItemHistory() {
      this.itemHistory.active = false
      this.itemHistory.product = {}
      this.itemHistory.type = ''
    },
    savePayment() {
      this.invoice.payments.push(Object.assign({}, this.editor.payment.data))
      this.editor.payment.active = false
      // ask to complete job
      if (this.isPaidFully) {
        // ask to set settlement date
        this.settle('Invoice paid fully, set as settled?')
      } else {
        this.save()
      }
    },
    setContactType(type) {
      if (this.contactType === type) {
        return
      }

      this.invoice.contact = {}
      this.list.customer = []
      this.list.supplier = []
      this.contactType = type
      this.invoice.type = this.contactType === 'supplier' ? 'purchases' : 'sales'

      // will disable type selection if there is an item inserted.
      if (this.contactType === this.initial.contact.type) {
        this.invoice.contact = Object.assign({}, this.initial.contact)
        this.list[this.contactType].push({id: this.invoice.contact.id, display_name: this.invoice.contact.display_name})
        return
      }

      // search and populate selected list in model-select
      this.$refs[this.contactType + 'ModelSelect'].search('')
    },
    setContact(contact) {
      this.invoice.contact_id = contact.id
      this.supplierAccount = {}
      if (this.invoice.type === 'sales') {
        this.save()
        return
      }
      // get supplier accounts
      axios.get('supplier-accounts', {params:{
        filters: {
          supplier_id: contact.id
        }
      }}).then(response => {
        this.list.supplierAccount = response.data.data.map(e => {
          return {id: e.id, display_name: '#' + e.id + ' ' + e.name + ' ' + e.code}
        })
      })
    },
    setSupplierAccount(supplierAccount) {
      this.invoice.supplier_account_id = supplierAccount.id
      this.save()
    },
    setMode() {
      const name = this.$route.name.split('.')
      if (name[0] === 'invoice' && name[1]) {
        this.mode = name[1]
      } else {
        this.mode = 'create'
      }

      if (this.invoiceID) {
        this.editID = this.invoiceID
        this.mode = 'edit'
      } else if (this.$route.params.invoiceID) {
        this.editID = this.$route.params.invoiceID
      }

      if (this.mode === 'create') {
        this.invoice = this.emptyInvoice()
      } else {
        // get invoice
        if (this.$route.name === 'invoice.preview') {
          this.preview = true
        }
        this.get()
      }
    },
    emptyInvoice() {
      return {
        contact_id: this.contactID,
        job_id: this.job ? this.job.id : null,
        supplier_account_id: null,
        created_at: this.$moment().format(this.config.dateTimeFormats.api),
        external_id: '',
        type: this.invoiceType,
        note: null,
        cash_on_delivery: false,
        settlement_date: null,
        items: [],
        payments: [],
      }
    },
    settle(message) {
      this.$swal.fire({
        title: 'Settle invoice',
        text: message || '',
        type: 'question',
        // input: 'checkbox',
        // inputValue: this.invoice.type === 'sales',
        // inputPlaceholder: 'Cash on delivery',
        confirmButtonText: 'Yes',
        showCancelButton: true,
        cancelButtonText: 'No',
      }).then(result => {
        if (typeof result.value !== 'undefined') {
          // this.invoice.cash_on_delivery = !!result.value
          // settle here
          this.invoice.settlement_date = this.$moment().format(this.config.dateTimeFormats.api)
        }
        this.save().then(() => {
          this.$emit('invoice-settled')
        })
      })
    },
    get() {
      return new Promise((resolve, reject) => {
        axios.get(path.join('invoices', String(this.editID)))
          .then((response) => {
            const invoice = response.data
            this.invoice = Object.assign({}, invoice)
            this.contactType = this.invoice.contact.type

            this.invoice.contact.display_name = '#' + this.invoice.contact.id + ' ' + this.invoice.contact.first_name + ' ' + this.invoice.contact.last_name + (this.invoice.contact.company_name ? ' (' + this.invoice.contact.company_name + ')' : '')

            this.initial.contact = Object.assign({}, this.invoice.contact)
            this.initial.external_id = this.invoice.external_id

            if (this.invoice.type === 'purchases') {
              // set supplier accounts
              this.list.supplierAccount = this.invoice.contact.supplier_accounts.map(e => {
                return {id: e.id, display_name: '#' + e.id + ' ' + e.name + ' ' + e.code}
              })
              // set selected supplier account
              this.supplierAccount = {id: this.invoice.supplier_account_id, display_name: (() => {
                let selected = this.invoice.contact.supplier_accounts.filter(sa => sa.id === this.invoice.supplier_account_id)[0]
                return '#' + selected.id + ' ' + selected.name + ' ' + selected.code
              })()}
            }

            this.$setInvoiceTotals(this.invoice, true)
            let creditNotesRemainingTotal = this.invoice.credit_notes.map(e => e.total.remaining).reduce((a, b) => a + b, 0)
            this.$set(this.invoice, 'credit_notes_remaining_total', creditNotesRemainingTotal)

            if (this.job && this.job.id) {
              this.$set(this.invoice, 'job', this.job)
            } else if (this.invoice.job_id){
              // get job
              axios.get(path.join('jobs', String(this.invoice.job_id)))
                .then(response => {
                  this.$set(this.invoice, 'job', response.data)
                })
            }

            // set payment type (editor)
            this.editor.payment.type = this.invoice.type === 'purchases' ? 'expense' : 'income'
            resolve(invoice)
          })
          .catch((error) => reject(error))
      })
    },
    setPurchaseInvoice(invoice){
      this.purchaseInvoice = invoice
    },
    getPurchaseInvoice(query) {
      let filters = {'type': 'purchases'}

      axios.get('invoices', {params: {
        search: query,
        filters
      }})
        .then(response => {
          let purchases = response.data.data

          for(let i in purchases) {
            purchases[i].display_name = '#' + purchases[i].id + ' created at: ' + this.$moment(purchases[i].created_at).format(this.config.dateFormats.display)
          }

          this.list.purchase = Object.assign([], purchases)
        })
    },
    save(callback) {
      if (callback) {
        callback(this.save())
        return
      }
      return new Promise((resolve, reject) => {
        if (!this.invoice.id) {
          axios.post('invoices', this.invoice)
            .then((response) => {
              this.$router.push('/invoice/edit/' + response.data.id)
              this['config/getFinancialAccounts']()
              this.$emit('created')
              resolve()
            })
            .catch((error) => reject(error))
        } else {
          axios.put('invoices/' + this.editID, this.invoice)
            .then(() => {
              this.get()
              this['config/getFinancialAccounts']()
              this.$emit('updated')
              resolve()
            })
            .catch((error) => reject(error))
        }
      })
    },
    copyInvoice() {
      this.purchaseInvoice.items.map(item => {
        item.invoice_id = this.invoice.id
        item.value = item.product.sale_price
        item.tax = item.product.sale_tax
        item.discount = 0
        item.description = ''
      })

      for(let i in this.purchaseInvoice.items) {
        this.invoice.items.push(Object.assign({}, this.purchaseInvoice.items[i]))
      }

      this.save().then(() => {
        this.purchaseInvoice = {}
      })

      this.modal.purchases.active = false

    },
    openPurchaseModal() {
      this.getPurchaseInvoice()
      this.modal.purchases.active = true
    },
    print() {
      // we could store logodata in data()
      // however loading the logo on each print request ensures latest logo usage
      this.invoiceLogoData()
        .then((data) => {
          this._print(data)
        })
    },
    _print(invoiceLogoData) {
      this.editor.item.active = false

      let rows = []
      rows.push([
        {text: 'Product', bold: true},
        {text: 'Description', bold: true},
        {text: 'Quantity', bold: true},
        {text: 'Value', bold: true},
        {text: 'Discount', bold: true},
        {text: 'Tax', bold: true},
        {text: 'Total', bold: true},
      ])
      for (let i in this.invoice.items) {
        let item = this.invoice.items[i]
        rows.push([
          item.product.name,
          item.description,
          item.quantity,
          this.$formatMoney(item.value),
          item.discount + '%',
          item.tax + '%',
          this.$formatMoney(item.total.all) + '\n' +
          (item.total.discount ? 'Discount: ' + this.$formatMoney(item.total.discount) + '\n' : '') +
          (item.total.tax ? 'Tax: ' + this.$formatMoney(item.total.tax) + '\n' : ''),
        ])
      }

      let addresses = {width: '*', text: ''}
      let addressList = []
      for (let i in this.invoice.contact.addresses) {
        let addr = this.invoice.contact.addresses[i]
        addressList.push(addr.address_1 + ',\n' + addr.city + ', ' + addr.state + ', ' + addr.post_code + ',\n' + this.CountryByID(addr.country_id).name)
      }
      addresses.text = addressList.join('\n')

      let phoneNumbers = {width: '*', text: ''}
      let phoneNumberList = []
      for (let i in this.invoice.contact.phone_numbers) {
        let phone = this.invoice.contact.phone_numbers[i]
        phoneNumberList.push('+' + phone.countryData.calling_code + ' ' + phone.number)
      }
      phoneNumbers.text = phoneNumberList.join('\n')

      let companyVATText = [
        { text: '\nVAT: ', fontSize: 9, bold: true },
        { text: this.config.companyVATNumber, fontSize: 9 },
      ]

      let companyIBANText = [
        { text: '\nIBAN: ', fontSize: 9, bold: true },
        { text: this.config.companyIBAN, fontSize: 9 },
      ]

      let companyAccountNumberText = [
        { text: '\nAccount No: ', fontSize: 9, bold: true },
        { text: this.config.companyAccountNumber, fontSize: 9 },
      ]

      var docDefinition = {
        content: [
          {
            columns: [
              {
                width: 90,
                image: invoiceLogoData,
              },
              {
                width: '*',
                text: '',
              },
              {
                width: '25%',
                text: (this.invoice.cash_on_delivery ? 'Cash ' : '') + 'Invoice',
                fontSize: 20,
                bold: true,
                alignment: 'right'
              }
            ],
          },
          {
            columns: [
              {
                width: '*',
                text: '',
              },
              {
                width: '25%',
                text: [
                  '#' + this.invoice.id + '\n' + 'Date: ',
                  this.$moment(this.invoice.created_at).format(this.config.dateFormats.display),
                  '\nStatus: ', {text: (this.isSettled ? 'Paid' : 'Unpaid'), bold: true}
                ],
                alignment: 'right'
              }
            ],
            margin: [ 0, 0, 0, 30 ],
          },
          {
            columns: [
              {
                text: [
                  {text: this.invoice.contact.first_name + ' ' + this.invoice.contact.last_name + (this.invoice.contact.company_name ? ' ' + this.invoice.contact.$ : ''), bold: true, fontSize: 12},
                  (this.supplierAccount.id ? '\n' + this.supplierAccount.display_name : ''),
                  (this.invoice.job && this.invoice.job.vehicle ? '\n' + this.invoice.job.vehicle.make + ' ' + this.invoice.job.vehicle.model + '\nPlate numbers: ' + this.invoice.job.vehicle.plate_numbers + '\nOdometer reading at date: ' + this.invoice.job.odometer_reading_at_date.toLocaleString(): ''),
                ]
              },
              {
                width: '*',
                text: '',
              },
            ],
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515 - 25*515/100, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 5, 0, 5 ]
          },
          {
            columns: [
              addresses,
              phoneNumbers,
              {
                width: '30%',
                text: [
                  { text: this.config.companyName + '\n', bold: true, fontSize: 10 },
                  { text: this.config.companyAddress + ' ' + this.config.companyCity + '\n', fontSize: 9 },
                  { text: (this.config.companyState ? this.config.companyState + ', ' : '') + ' ' + this.config.companyPostCode + ' ' + this.config.companyCountry + '\n', fontSize: 9 },
                  { text: this.config.companyPhone, fontSize: 9 },
                  ...this.config.companyVATNumber ? companyVATText : [],
                  ...this.config.companyIBAN ? companyIBANText : [],
                  ...this.config.companyAccountNumber ? companyAccountNumberText : [],
                ],
                alignment: 'right'
              }
            ],
            columnGap: 10
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 15, 0, 5 ]
          },
          {
            layout: 'lightHorizontalLines', // optional
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: ['*', 'auto', 'auto', 50, 50, 50, 100],
              // widths: [ 'auto', '*', 'auto', 100, '*' ],
              body: [
                ...rows,
              ]
            },
            // left, top, right, bottom
            margin: [ 0, 20, 0, 20 ],
          },
          {
            columns: [
              {
                width: '*',
                text: ''
              },
              {
                width: 'auto',
                layout: 'noBorders',
                table: {
                  headerRows: 0,
                  widths: ['auto', 'auto'],
                  body: [
                    ['Sub total:', this.$formatMoney(this.total.sub)],
                    ['Discount:', this.$formatMoney(this.total.discount)],
                    ['Tax:', this.$formatMoney(this.total.tax)],
                    [
                      {},
                      {canvas: [{ type: 'line', x1: 0, y1: 5, x2: 50, y2: 5, lineWidth: 1 }]},
                    ],
                    [
                      {text: 'Grant total: ', bold: true, fontSize: 11},
                      {text: this.$formatMoney(this.total.sub - this.total.discount + this.total.tax), bold: true, fontSize: 11},
                    ],
                  ],
                },
              }
            ],
            margin: [ 0, 0, 0, 20 ]
          },
          {
            columns: [
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Issuer\'s signature'],
                  ],
                }
              },
              {
                width: '*',
                text: ''
              },
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Receiver\'s signature'],
                  ],
                }
              },
            ],
          },
        ],
        footer(currentPage, pageCount) {
          if (pageCount === currentPage) {
            return {
              text: 'Thank you for doing business with us',
              alignment: 'center',
            }
          }
        },
        pageSize: 'A4',
        defaultStyle: {
          fontSize: 9,
          // bold: true
        }
      }

      pdfMake.createPdf(docDefinition).open()
    },
    invoiceLogoData() {
      return axios.get(this.config.logoPrintPath + '?_', {
        responseType: 'arraybuffer'
      })
        .then(response => {
          let image = btoa(
            new Uint8Array(response.data)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          )
          return `data:${response.headers['content-type'].toLowerCase()};base64,${image}`
        })
    },
    ...mapActions([
      'config/getFinancialAccounts'
    ])
  }
}
</script>

